import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ReEvent, Id, ReSession, SessionStatus } from "../interfaces/types";
import { addOrUpdateSessionInFirebase, fetchAllSessions } from "../services/firebaseService";
import { useEventContext } from "./EventProvider";
import { useUserContext } from "./userProvider";

export type SessionContextType = {
  sessions?: ReSession[];
  addSession: (newSession: ReSession, selectedEvent: ReEvent) => void;
  updateSession: (updatedSession: ReSession) => void;
  isTopicUsedBySession: (topic: Id) => boolean;
  isRoomSetupUsedBySession: (roomSetup: Id) => boolean;
  isDurationUsedBySession: (duration: Id) => boolean;
  getSessionById: (sessionId: Id) => ReSession | undefined;
  hasUserAtLeastOneSessionConfirmed: (userId: Id) => boolean;
};


const SessionContext = createContext<SessionContextType>({
  sessions: [],
  addSession: () => { },
  updateSession: () => { },
  isTopicUsedBySession: () => false,
  isRoomSetupUsedBySession: () => false,
  isDurationUsedBySession: () => false,
  getSessionById: () => undefined,
  hasUserAtLeastOneSessionConfirmed: () => false,
});

export const SessionProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<ReSession[]>([]);
  const { events, updateEvent } = useEventContext();
  const { user, userDetails } = useUserContext();

  useEffect(() => {
    if (user) {
      fetchAllSessions().then(fetchedSessions => {
        fetchedSessions = fetchedSessions?.filter(session => session.id);
        setSessions(fetchedSessions as ReSession[]);
      });
    }
  }, [user]);

  function addSession(newSession: ReSession, selectedEvent: ReEvent) {
    selectedEvent.sessions = [...selectedEvent.sessions, newSession.id];
    updateEvent(selectedEvent);
    addOrUpdateSessionInFirebase(newSession);
    setSessions([...sessions, newSession]);
  }

  function updateSession(updatedSession: ReSession) {
    addOrUpdateSessionInFirebase(updatedSession);

    setSessions((prevSessions) => {
      const updatedSessions = prevSessions.map(session => {
        if (session.id === updatedSession.id) {
          return updatedSession;
        }
        return session;
      });
      return updatedSessions;
    });
  }

  const isTopicUsedBySession = (topic: Id) => {
    return sessions?.some(session => session.topic === topic);
  };

  const isRoomSetupUsedBySession = (roomSetup: Id) => {
    return sessions?.some(session => session.roomSetup === roomSetup);
  };

  const isDurationUsedBySession = (duration: Id) => {
    return sessions?.some(session => session.duration === duration);
  };

  const getSessionById = (sessionId: Id) => {
    return sessions?.find(session => session.id === sessionId);
  };

  function hasUserAtLeastOneSessionConfirmed(userId: Id) {
    return sessions?.some(session => session.status === SessionStatus.CONFIRMED && session.presenterIds.includes(userId)) || false;
  }


  const value: SessionContextType = {
    sessions,
    addSession,
    updateSession,
    isTopicUsedBySession,
    isRoomSetupUsedBySession,
    isDurationUsedBySession,
    getSessionById,
    hasUserAtLeastOneSessionConfirmed,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};


export const useSessionContext = (): SessionContextType => {
  return useContext(SessionContext);
};